import React, { useState, useEffect, useCallback, useMemo, createContext, useContext, useRef } from 'react';
import { getMetadataFolders, MetadataFolder } from '../api';
import { checkAndHandleAuth } from '../services/authService';

export interface MetadataContextType {
    metadataFolders: MetadataFolder[];
    selectedMetadata: string[];
    excludedMetadata: string[];

    fetchingMetadata: boolean;
    metadataError: string | null;

    setExcludedMetadata: (metadata: string[]) => void;
    toggleFolderSelection: (folderPath: string, allChildPaths: string[]) => void;
    isMetadataSelected: (folderPath: string) => boolean;
    getSelectablePaths: () => string[];
    handleFetchMetadata: () => Promise<void>;
    selectAllMetadata: () => void;
    unselectAllMetadata: () => void;
    getSelectAllState: () => { checked: boolean; indeterminate: boolean };
}

const MetadataContext = createContext<MetadataContextType | undefined>(undefined);

export const useMetadataContext = () => {
    const context = useContext(MetadataContext);
    if (context === undefined) {
        throw new Error('useMetadataContext must be used within a MetadataProvider');
    }
    return context;
};

export const MetadataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [metadataFolders, setMetadataFolders] = useState<MetadataFolder[]>([]);
    const [excludedMetadata, setExcludedMetadata] = useState<string[]>([]);
    const [fetchingMetadata, setFetchingMetadata] = useState(false);
    const [metadataError, setMetadataError] = useState<string | null>(null);
    const metadataRequestRef = useRef(0);

    const flattenFolders = useCallback((folders: MetadataFolder[], parentPath = ""): string[] => {
        const paths: string[] = [];
        folders.forEach(folder => {
            const currentPath = parentPath ? `${parentPath}/${folder.folderName}` : folder.folderName;
            paths.push(currentPath);
            if (folder.subfolders && folder.subfolders.length > 0) {
                paths.push(...flattenFolders(folder.subfolders, currentPath));
            }
        });
        return paths;
    }, []);

    const getSelectablePaths = useCallback(() => {
        if (!metadataFolders || metadataFolders.length === 0) return [];
        return flattenFolders(metadataFolders);
    }, [metadataFolders, flattenFolders]);

    const isMetadataSelected = useCallback((folderPath: string) => {
        return !excludedMetadata.includes(folderPath);
    }, [excludedMetadata]);

    const selectedMetadata = useMemo(() => {
        const allPaths = getSelectablePaths();
        return allPaths.filter(path => !excludedMetadata.includes(path));
    }, [getSelectablePaths, excludedMetadata]);

    const toggleFolderSelection = useCallback((folderPath: string, allChildPaths: string[]) => {
        const isCurrentlySelected = !excludedMetadata.some(path => allChildPaths.includes(path));

        if (isCurrentlySelected) {
            // Unselect: add all child paths to excluded list
            const newExcluded = [...new Set([...excludedMetadata, ...allChildPaths])];
            setExcludedMetadata(newExcluded);
        } else {
            // Select: remove all child paths from excluded list
            const newExcluded = excludedMetadata.filter(path => !allChildPaths.includes(path));
            setExcludedMetadata(newExcluded);
        }
    }, [excludedMetadata]);

    const handleFetchMetadata = useCallback(async () => {
        const requestId = ++metadataRequestRef.current;

        setFetchingMetadata(true);
        setMetadataError(null);

        try {
            const folders = await getMetadataFolders();
            if (requestId !== metadataRequestRef.current) return;
            setMetadataFolders(folders);
        } catch (error) {
            if (requestId !== metadataRequestRef.current) return;
            console.error("Failed to fetch metadata folders:", error);
            setMetadataError("Failed to load metadata folders");

            if (error instanceof Error && error.message === "Authentication Required") {
                checkAndHandleAuth();
            }
        } finally {
            if (requestId === metadataRequestRef.current) {
                setFetchingMetadata(false);
            }
        }
    }, []);

    const selectAllMetadata = useCallback(() => {
        setExcludedMetadata([]);
    }, []);

    const unselectAllMetadata = useCallback(() => {
        const allPaths = getSelectablePaths();
        setExcludedMetadata(allPaths);
    }, [getSelectablePaths]);

    const getSelectAllState = useCallback(() => {
        const allPaths = getSelectablePaths();
        const selectedCount = selectedMetadata.length;

        if (selectedCount === 0) {
            return { checked: false, indeterminate: false };
        } else if (selectedCount === allPaths.length) {
            return { checked: true, indeterminate: false };
        } else {
            return { checked: false, indeterminate: true };
        }
    }, [getSelectablePaths, selectedMetadata]);

    useEffect(() => {
        handleFetchMetadata();
    }, []);

    const contextValue: MetadataContextType = {
        metadataFolders,
        selectedMetadata,
        excludedMetadata,

        fetchingMetadata,
        metadataError,

        setExcludedMetadata,
        toggleFolderSelection,
        isMetadataSelected,
        getSelectablePaths,
        handleFetchMetadata,
        selectAllMetadata,
        unselectAllMetadata,
        getSelectAllState,
    };

    return (
        <MetadataContext.Provider value={contextValue}>
            {children}
        </MetadataContext.Provider>
    );
};
